import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Form, Input, Select } from "antd";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { EAffiliateCurrency } from "@devowl-wp/api-real-commerce";
import { useStores } from "../../../../../../store/stores.js";
import { __ } from "../../../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../../../utils/matomoMask.js";
import { AffiliatePanel } from "../affiliatePanel.js";
const AffiliatePaymentDetailsFieldSet = observer(({ form, isEdit })=>{
    const { accountV2Store: { session } } = useStores();
    const [isEditMode, setEditMode] = useState(false);
    const isFieldDisabled = isEdit && !isEditMode;
    useEffect(()=>{
        if (!isEdit || !session?.affiliate) {
            return;
        }
        if (isEditMode) {
            // Truncated fields must start empty when editing, see the backend
            // payment-details `truncate` masking.
            form.setFieldsValue({
                affiliate: {
                    paymentDetails: {
                        iban: "",
                        ukSortCode: "",
                        ukAccountNumber: "",
                        achRoutingNumber: "",
                        achAccountNumber: "",
                        isTruncate: false
                    }
                }
            });
        } else {
            const { affiliate: { paymentDetails: { accountHolder, currency, achAccountNumber, achRoutingNumber, iban, ukAccountNumber, ukSortCode } } } = session;
            form.setFieldsValue({
                affiliate: {
                    paymentDetails: {
                        accountHolder,
                        currency,
                        achAccountNumber,
                        achRoutingNumber,
                        iban,
                        ukAccountNumber,
                        ukSortCode,
                        isTruncate: true
                    }
                }
            });
        }
    }, [
        isEdit,
        session,
        form,
        isEditMode
    ]);
    return /*#__PURE__*/ _jsx(AffiliatePanel, {
        heading: __("Payment details for payouts"),
        subline: __("Where we send your commission payouts."),
        action: isEdit && /*#__PURE__*/ _jsx(Button, {
            size: "small",
            onClick: ()=>setEditMode((prev)=>!prev),
            children: isEditMode ? __("Cancel edit") : __("Edit")
        }),
        children: /*#__PURE__*/ _jsx(Form.Item, {
            noStyle: true,
            shouldUpdate: ({ affiliate: { paymentDetails: prevPd } }, { affiliate: { paymentDetails: nextPd } })=>prevPd?.currency !== nextPd?.currency,
            children: ({ getFieldValue })=>{
                const currency = getFieldValue([
                    "affiliate",
                    "paymentDetails",
                    "currency"
                ]);
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        isEdit && /*#__PURE__*/ _jsx(Form.Item, {
                            noStyle: true,
                            name: [
                                "affiliate",
                                "paymentDetails",
                                "isTruncate"
                            ],
                            children: /*#__PURE__*/ _jsx(Input, {
                                type: "hidden"
                            })
                        }),
                        /*#__PURE__*/ _jsx(Form.Item, {
                            label: __("Currency"),
                            extra: __("EUR payouts via SEPA are free; other currencies may incur fees per the ATC."),
                            required: true,
                            children: /*#__PURE__*/ _jsx(Form.Item, {
                                noStyle: true,
                                name: [
                                    "affiliate",
                                    "paymentDetails",
                                    "currency"
                                ],
                                rules: [
                                    {
                                        required: true,
                                        message: __("Please select the currency in which you would like to receive payouts!")
                                    }
                                ],
                                children: /*#__PURE__*/ _jsx(Select, {
                                    disabled: isFieldDisabled,
                                    children: Object.values(EAffiliateCurrency).map((currency)=>/*#__PURE__*/ _jsx(Select.Option, {
                                            value: currency,
                                            children: currency
                                        }, currency))
                                })
                            })
                        }),
                        currency !== undefined && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Form.Item, {
                                    name: [
                                        "affiliate",
                                        "paymentDetails",
                                        "accountHolder"
                                    ],
                                    label: __("Account holder"),
                                    required: true,
                                    rules: [
                                        {
                                            required: true,
                                            message: __("Please enter the full name of the account holder!")
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsx(Input, {
                                        disabled: isFieldDisabled,
                                        ...MATOMO_MASK
                                    })
                                }),
                                currency === EAffiliateCurrency.EUR ? /*#__PURE__*/ _jsx(Form.Item, {
                                    name: [
                                        "affiliate",
                                        "paymentDetails",
                                        "iban"
                                    ],
                                    label: __("IBAN"),
                                    required: true,
                                    rules: [
                                        {
                                            required: true,
                                            message: __("Please enter your IBAN!")
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsx(Input, {
                                        disabled: isFieldDisabled,
                                        ...MATOMO_MASK
                                    })
                                }) : currency === EAffiliateCurrency.GBP ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            name: [
                                                "affiliate",
                                                "paymentDetails",
                                                "ukSortCode"
                                            ],
                                            label: __("UK Sort code"),
                                            required: true,
                                            rules: [
                                                {
                                                    required: true,
                                                    message: __("Please enter your UK Sort code!")
                                                }
                                            ],
                                            children: /*#__PURE__*/ _jsx(Input, {
                                                disabled: isFieldDisabled,
                                                ...MATOMO_MASK
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            name: [
                                                "affiliate",
                                                "paymentDetails",
                                                "ukAccountNumber"
                                            ],
                                            label: __("Account number"),
                                            required: true,
                                            rules: [
                                                {
                                                    required: true,
                                                    message: __("Please enter your account number!")
                                                }
                                            ],
                                            children: /*#__PURE__*/ _jsx(Input, {
                                                disabled: isFieldDisabled,
                                                ...MATOMO_MASK
                                            })
                                        })
                                    ]
                                }) : currency === EAffiliateCurrency.USD ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            name: [
                                                "affiliate",
                                                "paymentDetails",
                                                "achRoutingNumber"
                                            ],
                                            label: __("ACH routing number"),
                                            required: true,
                                            rules: [
                                                {
                                                    required: true,
                                                    message: __("Please enter your ACH routing number!")
                                                }
                                            ],
                                            children: /*#__PURE__*/ _jsx(Input, {
                                                disabled: isFieldDisabled,
                                                ...MATOMO_MASK
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Form.Item, {
                                            name: [
                                                "affiliate",
                                                "paymentDetails",
                                                "achAccountNumber"
                                            ],
                                            label: __("Account number"),
                                            required: true,
                                            rules: [
                                                {
                                                    required: true,
                                                    message: __("Please enter your account number!")
                                                }
                                            ],
                                            children: /*#__PURE__*/ _jsx(Input, {
                                                disabled: isFieldDisabled,
                                                ...MATOMO_MASK
                                            })
                                        })
                                    ]
                                }) : null
                            ]
                        })
                    ]
                });
            }
        })
    });
});
export { AffiliatePaymentDetailsFieldSet };
